import { useState, useEffect } from "react";
import { callWithAuth, submitOnboarding } from "@/api/Api.ts";
import { tokenStorage } from "@/api/tokenStorage";
import {
  Onboarding,
  LearningReason,
  LanguageLearningChallenge,
  LanguageLevelOnboarding,
  PreferredTopic,
  ReferralSource,
} from "@shared_types";

export type Months = number;
export type Minutes = number;

// Type for onboarding data with specific field types
export type OnboardingFormData = {
  whyDoYouWantToLearn: LearningReason;
  whenDoYouWantToBeFluentBy: Months;
  howMuchCanYouStudyEachDay: Minutes;
  whatIsYourBiggestChallengeWithLearningLanguage: LanguageLearningChallenge;
  currentSpanishLevel: LanguageLevelOnboarding;
  preferredTopic: PreferredTopic;
  howDidYouHearAboutUs: ReferralSource;
};

const initialOnboardingState: OnboardingFormData = {
  whyDoYouWantToLearn: "no_value",
  whenDoYouWantToBeFluentBy: 12,
  howMuchCanYouStudyEachDay: 15,
  whatIsYourBiggestChallengeWithLearningLanguage: "no_value",
  currentSpanishLevel: "no_value",
  preferredTopic: "no_value",
  howDidYouHearAboutUs: "no_value",
};

const ONBOARDING_DATA_KEY = "onboardingData";

function parseStoredObject(raw: string): Record<string, unknown> {
  const parsed: unknown = JSON.parse(raw);
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    throw new Error("Invalid onboarding data");
  }
  return Object.fromEntries(Object.entries(parsed));
}

function readNumber(
  data: Record<string, unknown>,
  key: string,
  fallback: number,
): number {
  const value = data[key];
  return typeof value === "number" ? value : fallback;
}

function readLearningReason(
  data: Record<string, unknown>,
): OnboardingFormData["whyDoYouWantToLearn"] {
  switch (data.whyDoYouWantToLearn) {
    case "travel":
    case "work":
    case "friends_and_family":
    case "school":
    case "self_improvement":
    case "other":
    case "romance":
      return data.whyDoYouWantToLearn;
    default:
      return initialOnboardingState.whyDoYouWantToLearn;
  }
}

function readLearningChallenge(
  data: Record<string, unknown>,
): OnboardingFormData["whatIsYourBiggestChallengeWithLearningLanguage"] {
  switch (data.whatIsYourBiggestChallengeWithLearningLanguage) {
    case "no_time":
    case "where_to_start":
    case "staying_motivated":
    case "grammar_issues":
    case "speaking_practice":
    case "listening_practice":
    case "vocabulary_overload":
      return data.whatIsYourBiggestChallengeWithLearningLanguage;
    default:
      return initialOnboardingState.whatIsYourBiggestChallengeWithLearningLanguage;
  }
}

function readSpanishLevel(
  data: Record<string, unknown>,
): OnboardingFormData["currentSpanishLevel"] {
  switch (data.currentSpanishLevel) {
    case "im_new_to_the_language":
    case "i_know_some_common_words":
    case "i_can_have_basic_conversations":
    case "i_can_talk_about_various_topics":
    case "i_can_discuss_most_topics_in_detail":
      return data.currentSpanishLevel;
    default:
      return initialOnboardingState.currentSpanishLevel;
  }
}

function readPreferredTopic(
  data: Record<string, unknown>,
): OnboardingFormData["preferredTopic"] {
  switch (data.preferredTopic) {
    case "general":
    case "travel":
    case "food":
    case "small_talk":
    case "vlogs_grwms":
    case "sports":
    case "grammar":
    case "romance":
      return data.preferredTopic;
    default:
      return initialOnboardingState.preferredTopic;
  }
}

function readReferralSource(
  data: Record<string, unknown>,
): OnboardingFormData["howDidYouHearAboutUs"] {
  const value = data.howDidYouHearAboutUs;
  return typeof value === "string"
    ? (value as ReferralSource)
    : initialOnboardingState.howDidYouHearAboutUs;
}

function readStoredOnboardingFormData(raw: string): OnboardingFormData {
  const data = parseStoredObject(raw);
  return {
    whyDoYouWantToLearn: readLearningReason(data),
    whenDoYouWantToBeFluentBy: readNumber(
      data,
      "whenDoYouWantToBeFluentBy",
      initialOnboardingState.whenDoYouWantToBeFluentBy,
    ),
    howMuchCanYouStudyEachDay: readNumber(
      data,
      "howMuchCanYouStudyEachDay",
      initialOnboardingState.howMuchCanYouStudyEachDay,
    ),
    whatIsYourBiggestChallengeWithLearningLanguage: readLearningChallenge(data),
    currentSpanishLevel: readSpanishLevel(data),
    preferredTopic: readPreferredTopic(data),
    howDidYouHearAboutUs: readReferralSource(data),
  };
}

function toOnboardingPayload(onboardingData: OnboardingFormData): Onboarding {
  return {
    id: 0,
    userId: 0,
    whyDoYouWantToLearn: onboardingData.whyDoYouWantToLearn,
    whenDoYouWantToBeFluentBy: onboardingData.whenDoYouWantToBeFluentBy,
    howMuchCanYouStudyEachDay: onboardingData.howMuchCanYouStudyEachDay,
    whatIsYourBiggestChallengeWithLearningLanguage:
      onboardingData.whatIsYourBiggestChallengeWithLearningLanguage,
    currentSpanishLevel: onboardingData.currentSpanishLevel,
    preferredTopic: onboardingData.preferredTopic,
    howDidYouHearAboutUs: onboardingData.howDidYouHearAboutUs,
    createdAt: new Date().toISOString(),
  };
}

export function getStoredOnboardingForSubmission(): Onboarding {
  const raw = localStorage.getItem(ONBOARDING_DATA_KEY);
  if (!raw) {
    throw new Error("Missing onboarding data");
  }
  return toOnboardingPayload(readStoredOnboardingFormData(raw));
}

export const useOnboarding = () => {
  const [onboardingData, setOnboardingData] = useState<OnboardingFormData>(
    () => {
      const savedData = localStorage.getItem(ONBOARDING_DATA_KEY);
      return savedData
        ? readStoredOnboardingFormData(savedData)
        : initialOnboardingState;
    },
  );

  useEffect(() => {
    localStorage.setItem(ONBOARDING_DATA_KEY, JSON.stringify(onboardingData));
  }, [onboardingData]);

  // Update a specific field in the onboarding data
  const updateOnboardingField = <K extends keyof OnboardingFormData>(
    field: K,
    value: OnboardingFormData[K],
  ) => {
    setOnboardingData((prev) => {
      const newData = { ...prev, [field]: value };
      return newData;
    });
  };

  // Submit onboarding data to the server
  const submitOnboardingData = async (): Promise<void> => {
    if (!tokenStorage.isTokenSet()) {
      return;
    }
    try {
      await callWithAuth(submitOnboarding, toOnboardingPayload(onboardingData));
    } catch (error) {
      console.error("Error submitting onboarding data:", error);
      throw error;
    }
  };

  // Reset onboarding data to initial state
  const resetOnboarding = () => {
    setOnboardingData(initialOnboardingState);
    localStorage.removeItem(ONBOARDING_DATA_KEY);
  };

  return {
    onboardingData,
    updateOnboardingField,
    submitOnboarding: submitOnboardingData,
    resetOnboarding,
  };
};
